import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NotesIcon from '@mui/icons-material/Notes';
import FlagIcon from '@mui/icons-material/Flag';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useStore } from 'logic';
import { formatDistanceToNow, format } from 'date-fns';
import { Milestone } from 'logic/ui-project-milestones';
import { ResizableDrawerContent } from './ResizableDrawerContent';
import { ProjectNotes } from './ProjectNotes';
import { formatEmailToName, formatMilestoneType } from './utils/formatters';

const QueueProgress = observer(() => {
  const {
    sessionsStore: { currentSession },
  } = useStore();

  if (!currentSession) return null;

  const totalAssets = currentSession.inProgressTotalCount + currentSession.doneTotalCount;
  const completedAssets = currentSession.doneTotalCount;
  const inProgressAssets = currentSession.inProgressTotalCount;
  const progressPercentage = totalAssets > 0 ? (completedAssets / totalAssets) * 100 : 0;

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Queue Progress
      </Typography>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1,
          }}
        >
          <Typography
            variant="h1"
            sx={{ fontWeight: 700, fontSize: '1.875rem', whiteSpace: 'nowrap' }}
          >
            {Math.round(progressPercentage)}%
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`${totalAssets} CARDS`}
              sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                fontWeight: 700,
              }}
            />
            <Chip
              label={`${inProgressAssets} ACTIVE`}
              sx={{
                borderRadius: 2,
                backgroundColor: (theme) => theme.palette.primary.main,
                fontWeight: 700,
              }}
            />
            <Chip
              label={`${completedAssets} DONE`}
              sx={{
                borderRadius: 2,
                backgroundColor: (theme) => theme.palette.success.main,
                fontWeight: 700,
              }}
            />
          </Box>
        </Box>
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
});

const ProjectMetrics = observer(() => {
  const {
    sessionsStore: { currentSession },
    uiTimeSavedStore,
    commentsStore,
  } = useStore();

  if (!currentSession) return null;

  const timeSaved = Math.floor(uiTimeSavedStore.timeSavedInSeconds / 60); // Convert to minutes
  const commentCount = Object.keys(commentsStore.comments).length;
  const filesNeedingAttention = currentSession.numFilenamesNeedingAttention;

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Metrics
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <AccessTimeIcon color="primary" sx={{ fontSize: '2rem' }} />
          <Box>
            <Typography
              variant="h1"
              sx={{ fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.2 }}
            >
              {timeSaved}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              minutes saved
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <GroupIcon color="primary" sx={{ fontSize: '2rem' }} />
          <Box>
            <Typography
              variant="h1"
              sx={{ fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.2 }}
            >
              {commentCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              comments
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <WarningIcon
            color={filesNeedingAttention > 0 ? 'warning' : 'disabled'}
            sx={{ fontSize: '2rem' }}
          />
          <Box>
            <Typography
              variant="h1"
              sx={{ fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.2 }}
            >
              {filesNeedingAttention}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              warnings
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

const MetricsView = observer(() => (
  <Box>
    <QueueProgress />
    <ProjectMetrics />
  </Box>
));

const NotesView = observer(() => {
  const { uiProjectNotesStore } = useStore();

  return (
    <Box>
      <ProjectNotes
        content={uiProjectNotesStore.currentProjectNotes}
        onChange={(content: string) => uiProjectNotesStore.setProjectNotes(content)}
      />
    </Box>
  );
});

const MilestonesView = observer(() => {
  const { uiProjectMilestonesStore } = useStore();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [openViewDialog, setOpenViewDialog] = React.useState(false);
  const [selectedMilestone, setSelectedMilestone] = React.useState<Milestone | null>(null);
  const [editingMilestone, setEditingMilestone] = React.useState<string | null>(null);
  const [newMilestone, setNewMilestone] = React.useState<Partial<Milestone>>({
    type: 'review',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    status: 'todo',
  });
  const [copyAnchorEl, setCopyAnchorEl] = React.useState<null | HTMLElement>(null);
  const openCopyMenu = Boolean(copyAnchorEl);

  // Calculate progress
  const totalMilestones = uiProjectMilestonesStore.sortedMilestones.length;
  const completedMilestones = uiProjectMilestonesStore.sortedMilestones.filter(
    (m) => m.status === 'done',
  ).length;
  const inProgressMilestones = uiProjectMilestonesStore.sortedMilestones.filter(
    (m) => m.status === 'inProgress',
  ).length;
  const todoMilestones = totalMilestones - completedMilestones - inProgressMilestones;
  const progressPercentage =
    totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0;

  const handleViewMilestone = (milestone: Milestone, event: React.MouseEvent) => {
    // Don't open view dialog if clicking on action buttons
    if (
      event.target instanceof Element &&
      (event.target.closest('button') || event.target.closest('.MuiIconButton-root'))
    ) {
      return;
    }
    setSelectedMilestone(milestone);
    setOpenViewDialog(true);
  };

  const handleAddMilestone = async () => {
    if (newMilestone.title && newMilestone.startDate && newMilestone.endDate && newMilestone.type) {
      const milestoneData = {
        title: newMilestone.title,
        startDate: newMilestone.startDate,
        endDate: newMilestone.endDate,
        type: newMilestone.type as
          | 'review'
          | 'deadline'
          | 'approval'
          | 'qc'
          | 'retrospective'
          | 'delivery'
          | 'launch'
          | 'planning'
          | 'production'
          | 'other',
        description: newMilestone.description,
        customType: newMilestone.type === 'other' ? newMilestone.customType : undefined,
        ownedBy: newMilestone.ownedBy,
        informed: newMilestone.informed,
        status: 'todo' as const,
      };

      try {
        if (editingMilestone) {
          const existingMilestone = uiProjectMilestonesStore.sortedMilestones.find(
            (m) => m.id === editingMilestone,
          );
          await uiProjectMilestonesStore.updateMilestone(editingMilestone, {
            ...milestoneData,
            status: existingMilestone?.status || 'todo',
            isCompleted: existingMilestone?.isCompleted || false,
            completedAt: existingMilestone?.completedAt,
          });

          // Get the updated milestone from the store
          const updatedMilestone = uiProjectMilestonesStore.sortedMilestones.find(
            (m: Milestone) => m.id === editingMilestone,
          );
          if (updatedMilestone) {
            // Small delay to ensure proper transition between dialogs
            setTimeout(() => {
              setSelectedMilestone(updatedMilestone);
              setOpenViewDialog(true);
            }, 100);
          }
        } else {
          await uiProjectMilestonesStore.addMilestone(milestoneData);
        }

        setOpenDialog(false);
        setEditingMilestone(null);
        setNewMilestone({
          type: 'review',
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(new Date(), 'yyyy-MM-dd'),
        });
      } catch (error) {
        console.error('Failed to save milestone:', error);
      }
    }
  };

  const handleEditMilestone = (milestone: Milestone) => {
    setEditingMilestone(milestone.id);
    setNewMilestone({
      title: milestone.title,
      startDate: milestone.startDate,
      endDate: milestone.endDate,
      type: milestone.type,
      description: milestone.description,
      customType: milestone.customType,
      ownedBy: milestone.ownedBy,
      informed: milestone.informed,
      status: milestone.status || 'todo',
    });
    setOpenDialog(true);
  };

  const handleDeleteMilestone = async (id: string) => {
    await uiProjectMilestonesStore.deleteMilestone(id);
  };

  const handleToggleCompletion = async (id: string) => {
    await uiProjectMilestonesStore.toggleMilestoneCompletion(id);
  };

  const getMilestoneTypeColor = (type: string) => {
    switch (type) {
      case 'review':
      case 'retrospective':
      case 'planning':
        return 'primary';
      case 'deadline':
      case 'delivery':
      case 'launch':
        return 'error';
      case 'approval':
        return 'success';
      case 'qc':
      case 'production':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Helper function to handle email list changes
  const handleEmailListChange =
    (field: 'ownedBy' | 'informed') => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const emails = e.target.value
        .split(/[,\n]/)
        .map((email) => email.trim())
        .filter(Boolean);
      setNewMilestone({ ...newMilestone, [field]: emails });
    };

  // Get milestone markdown format
  const getMilestoneMarkdown = (milestone: Milestone): string => {
    const lines: string[] = [`# ${milestone.title}`, ''];

    // Type
    lines.push(
      `**Type:** ${
        milestone.type === 'other' && milestone.customType ? milestone.customType : milestone.type
      }`,
      '',
    );

    // Timeline with separate Start and End dates
    lines.push(
      '**Timeline:**',
      `Start: ${format(new Date(milestone.startDate), 'MMM d, yyyy h:mm a')}`,
      `End: ${format(new Date(milestone.endDate), 'MMM d, yyyy h:mm a')}`,
      '',
    );

    // Assigned To
    if (milestone.ownedBy && milestone.ownedBy.length > 0) {
      lines.push(
        '**Assigned to:**',
        milestone.ownedBy.map((email) => `- ${formatEmailToName(email)} (${email})`).join('\n'),
        '',
      );
    }

    // Description
    if (milestone.description) {
      lines.push('**Description:**', milestone.description, '');
    }

    // Informed
    if (milestone.informed && milestone.informed.length > 0) {
      lines.push(
        '**Informed:**',
        milestone.informed.map((email) => `- ${formatEmailToName(email)} (${email})`).join('\n'),
        '',
      );
    }

    // Created and Completed timestamps
    lines.push(`**Created:** ${format(milestone.createdAt, 'MMM d, yyyy h:mm a')}`);
    if (milestone.isCompleted && milestone.completedAt !== undefined) {
      lines.push(`**Completed:** ${format(new Date(milestone.completedAt), 'MMM d, yyyy h:mm a')}`);
    }

    return lines.join('\n');
  };

  // Get plaintext version of milestone
  const getMilestonePlaintext = (milestone: Milestone): string => {
    const lines: string[] = [milestone.title, ''];

    // Type
    lines.push(
      `Type: ${
        milestone.type === 'other' && milestone.customType ? milestone.customType : milestone.type
      }`,
      '',
    );

    // Timeline with separate Start and End dates
    lines.push(
      'Timeline:',
      `Start: ${format(new Date(milestone.startDate), 'MMM d, yyyy h:mm a')}`,
      `End: ${format(new Date(milestone.endDate), 'MMM d, yyyy h:mm a')}`,
      '',
    );

    // Assigned To
    if (milestone.ownedBy && milestone.ownedBy.length > 0) {
      lines.push(
        'Assigned to:',
        ...milestone.ownedBy.map((email) => `${formatEmailToName(email)} (${email})`),
        '',
      );
    }

    // Description
    if (milestone.description) {
      lines.push('Description:', milestone.description, '');
    }

    // Informed
    if (milestone.informed && milestone.informed.length > 0) {
      lines.push(
        'Informed:',
        ...milestone.informed.map((email) => `${formatEmailToName(email)} (${email})`),
        '',
      );
    }

    // Created and Completed timestamps
    lines.push(`Created: ${format(milestone.createdAt, 'MMM d, yyyy h:mm a')}`);
    if (milestone.isCompleted && milestone.completedAt !== undefined) {
      lines.push(`Completed: ${format(new Date(milestone.completedAt), 'MMM d, yyyy h:mm a')}`);
    }

    return lines.join('\n');
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = async (milestone: Milestone) => {
    const markdown = getMilestoneMarkdown(milestone);
    try {
      await navigator.clipboard.writeText(markdown);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleCopyClose = () => {
    setCopyAnchorEl(null);
  };

  // Group milestones by status
  const groupedMilestones = React.useMemo(() => {
    const groups = {
      todo: [] as Milestone[],
      inProgress: [] as Milestone[],
      done: [] as Milestone[],
    };

    uiProjectMilestonesStore.sortedMilestones.forEach((milestone) => {
      // Use the status field directly, defaulting to 'todo' if not set
      const status = milestone.status || 'todo';
      groups[status].push(milestone);
    });

    return groups;
  }, [uiProjectMilestonesStore.sortedMilestones]);

  // Add this helper function near the top of the file
  const StatusSelect = ({
    status,
    onChange,
    size = 'small',
  }: {
    status: 'todo' | 'inProgress' | 'done' | undefined;
    onChange: (status: 'todo' | 'inProgress' | 'done') => void;
    size?: 'small' | 'medium';
  }) => (
    <Select
      value={status || 'todo'}
      size={size}
      onChange={(e) => {
        const newStatus = e.target.value as 'todo' | 'inProgress' | 'done';
        onChange(newStatus);
      }}
      sx={{
        height: size === 'small' ? '24px' : '32px',
        backgroundColor:
          status === 'done'
            ? 'success.main'
            : status === 'inProgress'
            ? 'primary.main'
            : 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        '& .MuiSelect-select': {
          py: size === 'small' ? 0 : 0.5,
          pl: 1,
        },
      }}
    >
      <MenuItem value="todo">To Do</MenuItem>
      <MenuItem value="inProgress">In Progress</MenuItem>
      <MenuItem value="done">Done</MenuItem>
    </Select>
  );

  // Update the renderMilestone function to replace the Chip with StatusSelect
  const renderMilestone = (milestone: Milestone) => (
    <ListItem
      key={milestone.id}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        '&:hover': {
          bgcolor: 'action.hover',
          cursor: 'pointer',
          '& .milestone-actions': {
            opacity: 1,
            visibility: 'visible',
          },
        },
        opacity: milestone.isCompleted ? 0.7 : 1,
        p: 0,
        flexDirection: 'row',
        alignItems: 'flex-start',
      }}
      onClick={(e) => {
        // Only open view dialog if not clicking the status select
        if (!(e.target as Element).closest('.MuiSelect-root')) {
          handleViewMilestone(milestone, e);
        }
      }}
    >
      <Tooltip
        title="Mark as Done"
        disableInteractive
        arrow
        open={false}
        onOpen={() => {}}
        onClose={() => {}}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleToggleCompletion(milestone.id);
          }}
          size="small"
          color={milestone.isCompleted ? 'success' : undefined}
          sx={{ mt: 0.5, ml: 1 }}
        >
          {milestone.isCompleted ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
        </IconButton>
      </Tooltip>

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            px: 2,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, lineHeight: 1 }}>
              {milestone.title}
            </Typography>
            <Box onClick={(e) => e.stopPropagation()}>
              <StatusSelect
                status={milestone.status || 'todo'}
                onChange={(newStatus) => {
                  uiProjectMilestonesStore.updateMilestone(milestone.id, {
                    ...milestone,
                    status: newStatus,
                    isCompleted: newStatus === 'done',
                    completedAt: newStatus === 'done' ? Date.now() : undefined,
                  });
                }}
              />
            </Box>
          </Box>
          <Box
            className="milestone-actions"
            sx={{
              display: 'flex',
              gap: 1,
              opacity: 0,
              visibility: 'hidden',
              transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            }}
          >
            <Tooltip
              title="Copy Milestone"
              disableInteractive
              arrow
              open={false}
              onOpen={() => {}}
              onClose={() => {}}
            >
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.setAttribute('data-milestone-id', milestone.id);
                  setCopyAnchorEl(e.currentTarget);
                }}
                size="small"
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              title="Edit Milestone"
              disableInteractive
              arrow
              open={false}
              onOpen={() => {}}
              onClose={() => {}}
            >
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditMilestone(milestone);
                }}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              title="Delete Milestone"
              disableInteractive
              arrow
              open={false}
              onOpen={() => {}}
              onClose={() => {}}
            >
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteMilestone(milestone.id);
                }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ px: 2, py: 1.5, width: '100%' }}>
          <Typography component="div" variant="body2" color="text.secondary">
            {milestone.isCompleted && milestone.completedAt !== undefined && (
              <Box
                sx={{
                  color: 'success.main',
                  mb: 1,
                }}
              >
                {`Completed ${formatDistanceToNow(new Date(milestone.completedAt))} ago`}
              </Box>
            )}
            {((milestone.ownedBy && milestone.ownedBy.length > 0) ||
              (milestone.informed && milestone.informed.length > 0)) && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    component="div"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Assigned to:
                  </Typography>
                  {milestone.ownedBy && milestone.ownedBy.length > 0 ? (
                    milestone.ownedBy.map((email) => (
                      <Chip
                        key={email}
                        label={formatEmailToName(email)}
                        size="small"
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No assignees
                    </Typography>
                  )}
                  <Box
                    sx={{
                      ml: 2,
                      display: 'flex',
                      gap: 0.5,
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      component="div"
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 'bold' }}
                    >
                      Type:
                    </Typography>
                    <Chip
                      label={
                        milestone.type === 'other' && milestone.customType
                          ? formatMilestoneType(milestone.customType)
                          : formatMilestoneType(milestone.type)
                      }
                      size="small"
                      color={getMilestoneTypeColor(milestone.type)}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Timeline:
                  </Typography>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {format(new Date(milestone.startDate), 'MMM d, yyyy')} -{' '}
                    {format(new Date(milestone.endDate), 'MMM d, yyyy')}
                  </Typography>
                </Box>

                {milestone.informed && milestone.informed.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Typography
                      component="div"
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 'bold' }}
                    >
                      Informed:
                    </Typography>
                    {milestone.informed.map((email) => (
                      <Chip
                        key={email}
                        label={formatEmailToName(email)}
                        size="small"
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
            {milestone.description && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  component="div"
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', mb: 0.5 }}
                >
                  Description:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {milestone.description.length > 300
                    ? `${milestone.description.slice(0, 300)}...`
                    : milestone.description}
                </Typography>
              </Box>
            )}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );

  return (
    <Box>
      <Box sx={{ width: '100%', mb: 3 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 1,
            }}
          >
            <Typography
              variant="h1"
              sx={{ fontWeight: 700, fontSize: '1.875rem', whiteSpace: 'nowrap' }}
            >
              {Math.round(progressPercentage)}%
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={`${totalMilestones} TOTAL`}
                sx={{
                  borderRadius: 2,
                  backgroundColor: '#FFFFFF',
                  color: '#000000',
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`${todoMilestones} TO DO`}
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`${inProgressMilestones} IN PROGRESS`}
                sx={{
                  borderRadius: 2,
                  backgroundColor: (theme) => theme.palette.primary.main,
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`${completedMilestones} DONE`}
                sx={{
                  borderRadius: 2,
                  backgroundColor: (theme) => theme.palette.success.main,
                  fontWeight: 700,
                }}
              />
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: (theme) => theme.palette.success.main,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: '100%', mb: 2 }}>
        <Button
          onClick={() => {
            setEditingMilestone(null);
            setNewMilestone({
              type: 'review',
              startDate: format(new Date(), 'yyyy-MM-dd'),
              endDate: format(new Date(), 'yyyy-MM-dd'),
            });
            setOpenDialog(true);
          }}
          variant="outlined"
          fullWidth
          startIcon={<AddIcon />}
          sx={{
            borderColor: 'divider',
            borderRadius: 1,
            height: 40,
            justifyContent: 'center',
            gap: 1,
          }}
        >
          Add Milestone
        </Button>
      </Box>

      {/* Todo Section */}
      {groupedMilestones.todo.length > 0 && (
        <List>{groupedMilestones.todo.map(renderMilestone)}</List>
      )}

      {/* In Progress Section */}
      {groupedMilestones.inProgress.length > 0 && (
        <List>{groupedMilestones.inProgress.map(renderMilestone)}</List>
      )}

      {/* Done Section */}
      {groupedMilestones.done.length > 0 && (
        <List>{groupedMilestones.done.map(renderMilestone)}</List>
      )}

      {/* View Milestone Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          setSelectedMilestone(null);
        }}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(2px)',
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(40, 40, 40, 1)',
            border: '2px solid rgba(255, 255, 255, 1)',
          },
        }}
      >
        {selectedMilestone && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontSize: '1.5rem' }}>
                    {selectedMilestone.title}
                  </Typography>
                  <StatusSelect
                    status={selectedMilestone.status || 'todo'}
                    onChange={(newStatus) => {
                      uiProjectMilestonesStore.updateMilestone(selectedMilestone.id, {
                        ...selectedMilestone,
                        status: newStatus,
                        isCompleted: newStatus === 'done',
                        completedAt: newStatus === 'done' ? Date.now() : undefined,
                      });
                    }}
                    size="medium"
                  />
                </Box>
                <IconButton
                  onClick={() => {
                    setOpenViewDialog(false);
                    setSelectedMilestone(null);
                  }}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  mt: 2,
                }}
              >
                <Box sx={{ display: 'flex', gap: 3 }}>
                  {/* Assigned To */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      Assigned to
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      {selectedMilestone.ownedBy && selectedMilestone.ownedBy.length > 0 ? (
                        selectedMilestone.ownedBy.map((email) => (
                          <Chip
                            key={email}
                            label={formatEmailToName(email)}
                            size="small"
                            sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No assignees
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Type */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      Type
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={
                          selectedMilestone.type === 'other' && selectedMilestone.customType
                            ? formatMilestoneType(selectedMilestone.customType)
                            : formatMilestoneType(selectedMilestone.type)
                        }
                        size="small"
                        color={getMilestoneTypeColor(selectedMilestone.type)}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Start and End Date */}
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      Start Date
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <CalendarTodayIcon fontSize="small" color="primary" />
                      <Typography>
                        {format(new Date(selectedMilestone.startDate), 'MMM d, yyyy h:mm a')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      End Date
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <CalendarTodayIcon fontSize="small" color="primary" />
                      <Typography>
                        {format(new Date(selectedMilestone.endDate), 'MMM d, yyyy h:mm a')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Description */}
                {selectedMilestone.description && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Description
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        borderRadius: 1,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'rgba(0, 0, 0, 0.1)',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '4px',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.3)',
                          },
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          fontSize: '0.9rem',
                        }}
                      >
                        {selectedMilestone.description}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Informed */}
                {selectedMilestone.informed && selectedMilestone.informed.length > 0 && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Informed
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      {selectedMilestone.informed.map((email) => (
                        <Chip
                          key={email}
                          label={formatEmailToName(email)}
                          size="small"
                          sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 2,
                pb: 2,
              }}
            >
              <Tooltip title="Delete" disableInteractive arrow>
                <Button
                  onClick={() => {
                    setOpenViewDialog(false);
                    handleDeleteMilestone(selectedMilestone.id);
                  }}
                  color="error"
                >
                  <DeleteIcon />
                </Button>
              </Tooltip>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Copy" disableInteractive arrow>
                  <Button
                    onClick={(e) => {
                      e.currentTarget.setAttribute('data-milestone-id', selectedMilestone.id);
                      setCopyAnchorEl(e.currentTarget);
                    }}
                  >
                    <ContentCopyIcon />
                  </Button>
                </Tooltip>
                <Tooltip title="Edit" disableInteractive arrow>
                  <Button
                    onClick={() => {
                      setOpenViewDialog(false);
                      handleEditMilestone(selectedMilestone);
                    }}
                  >
                    <EditIcon />
                  </Button>
                </Tooltip>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(2px)',
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(40, 40, 40, 1)',
            border: '2px solid rgba(255, 255, 255, 1)',
          },
        }}
        disablePortal
        keepMounted={false}
        disableEnforceFocus
        onBackdropClick={() => setOpenDialog(false)}
      >
        <DialogTitle>{editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
            }}
          >
            <TextField
              label="Title"
              fullWidth
              required
              variant="outlined"
              value={newMilestone.title || ''}
              onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
              autoFocus
            />

            <TextField
              label="Description (optional)"
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              value={newMilestone.description || ''}
              onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
            />

            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={newMilestone.type || ''}
                label="Type"
                onChange={(e) =>
                  setNewMilestone({
                    ...newMilestone,
                    type: e.target.value as
                      | 'review'
                      | 'deadline'
                      | 'approval'
                      | 'qc'
                      | 'retrospective'
                      | 'delivery'
                      | 'launch'
                      | 'planning'
                      | 'production'
                      | 'other',
                    customType: e.target.value === 'other' ? newMilestone.customType : undefined,
                  })
                }
              >
                <MenuItem value="planning">Planning Session</MenuItem>
                <MenuItem value="review">Review</MenuItem>
                <MenuItem value="approval">Approval</MenuItem>
                <MenuItem value="qc">QC</MenuItem>
                <MenuItem value="production">Production Day</MenuItem>
                <MenuItem value="retrospective">Retrospective</MenuItem>
                <MenuItem value="deadline">Deadline</MenuItem>
                <MenuItem value="delivery">Delivery</MenuItem>
                <MenuItem value="launch">Launch</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            {newMilestone.type === 'other' && (
              <TextField
                label="Custom Type"
                fullWidth
                variant="outlined"
                value={newMilestone.customType || ''}
                onChange={(e) =>
                  setNewMilestone({
                    ...newMilestone,
                    customType: e.target.value,
                  })
                }
                placeholder="Enter custom milestone type"
              />
            )}

            <TextField
              label="Assigned to"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              placeholder="Enter email addresses (one per line or comma-separated)"
              value={newMilestone.ownedBy?.join('\n') || ''}
              onChange={handleEmailListChange('ownedBy')}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ position: 'relative', width: '100%' }}>
                <TextField
                  label="Start Date"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={
                    newMilestone.startDate
                      ? format(new Date(newMilestone.startDate), 'MMM d, yyyy h:mm a')
                      : ''
                  }
                  onClick={() => {
                    const input = document.getElementById('start-date-input');
                    if (input instanceof HTMLInputElement)
                      (input as HTMLInputElement & { showPicker(): void }).showPicker();
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const input = document.getElementById('start-date-input');
                          if (input instanceof HTMLInputElement)
                            (input as HTMLInputElement & { showPicker(): void }).showPicker();
                        }}
                      >
                        <CalendarTodayIcon fontSize="small" />
                      </IconButton>
                    ),
                    readOnly: true,
                    sx: { cursor: 'pointer' },
                  }}
                  sx={{ cursor: 'pointer' }}
                />
                <input
                  type="datetime-local"
                  id="start-date-input"
                  value={
                    newMilestone.startDate
                      ? format(new Date(newMilestone.startDate), "yyyy-MM-dd'T'HH:mm")
                      : ''
                  }
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value).toISOString() : '';
                    setNewMilestone({ ...newMilestone, startDate: date });
                  }}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    zIndex: -1,
                  }}
                />
              </Box>
              <Box sx={{ position: 'relative', width: '100%' }}>
                <TextField
                  label="End Date"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={
                    newMilestone.endDate
                      ? format(new Date(newMilestone.endDate), 'MMM d, yyyy h:mm a')
                      : ''
                  }
                  onClick={() => {
                    const input = document.getElementById('end-date-input');
                    if (input instanceof HTMLInputElement)
                      (input as HTMLInputElement & { showPicker(): void }).showPicker();
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const input = document.getElementById('end-date-input');
                          if (input instanceof HTMLInputElement)
                            (input as HTMLInputElement & { showPicker(): void }).showPicker();
                        }}
                      >
                        <CalendarTodayIcon fontSize="small" />
                      </IconButton>
                    ),
                    readOnly: true,
                    sx: { cursor: 'pointer' },
                  }}
                  sx={{ cursor: 'pointer' }}
                />
                <input
                  type="datetime-local"
                  id="end-date-input"
                  value={
                    newMilestone.endDate
                      ? format(new Date(newMilestone.endDate), "yyyy-MM-dd'T'HH:mm")
                      : ''
                  }
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value).toISOString() : '';
                    setNewMilestone({ ...newMilestone, endDate: date });
                  }}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    zIndex: -1,
                  }}
                />
              </Box>
            </Box>

            <TextField
              label="Informed"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              placeholder="Enter email addresses (one per line or comma-separated)"
              value={newMilestone.informed?.join('\n') || ''}
              onChange={handleEmailListChange('informed')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setEditingMilestone(null);
              setNewMilestone({
                type: 'review',
                startDate: format(new Date(), 'yyyy-MM-dd'),
                endDate: format(new Date(), 'yyyy-MM-dd'),
              });
            }}
          >
            Cancel
          </Button>
          <Tooltip
            title="Needs a title"
            disableInteractive
            arrow
            open={!newMilestone.title ? undefined : false}
          >
            <span>
              <Button
                onClick={handleAddMilestone}
                variant="contained"
                disabled={!newMilestone.title || !newMilestone.startDate || !newMilestone.endDate}
              >
                {editingMilestone ? 'Save Changes' : 'Add'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Copy Menu */}
      <Menu
        anchorEl={copyAnchorEl}
        open={openCopyMenu}
        onClose={handleCopyClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            const milestone = uiProjectMilestonesStore.sortedMilestones.find(
              (m) => m.id === copyAnchorEl?.getAttribute('data-milestone-id'),
            );
            if (milestone) {
              handleCopyToClipboard(milestone);
            }
            handleCopyClose();
          }}
        >
          As Markdown
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            const milestone = uiProjectMilestonesStore.sortedMilestones.find(
              (m) => m.id === copyAnchorEl?.getAttribute('data-milestone-id'),
            );
            if (milestone) {
              navigator.clipboard.writeText(getMilestonePlaintext(milestone));
            }
            handleCopyClose();
          }}
        >
          As Plain Text
        </MenuItem>
      </Menu>
    </Box>
  );
});

export const ProjectDrawerView = observer(
  ({
    onBackToProjects,
    onClose,
    initialTab = 0,
  }: {
    onBackToProjects: () => void;
    onClose: () => void;
    initialTab?: number;
  }) => {
    const {
      sessionsStore: { currentSession },
    } = useStore();

    const [activeTab, setActiveTab] = React.useState(initialTab);

    if (!currentSession) return null;

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    };

    return (
      <ResizableDrawerContent defaultWidth={700}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton size="small" onClick={onBackToProjects}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h1" sx={{ fontWeight: 500, fontSize: '2rem' }}>
              {currentSession.data.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created {formatDistanceToNow(currentSession.data.createdAt)} ago by{' '}
              <Tooltip title={currentSession.data.createdBy?.email || 'Unknown'} arrow>
                <Box
                  component="span"
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textDecorationStyle: 'dotted',
                  }}
                >
                  {formatEmailToName(currentSession.data.createdBy?.email)}
                </Box>
              </Tooltip>
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontSize: '1rem',
              },
            }}
          >
            <Tab icon={<AssessmentIcon />} iconPosition="start" label="Status" />
            <Tab icon={<FlagIcon />} iconPosition="start" label="Milestones" />
            <Tab icon={<NotesIcon />} iconPosition="start" label="Notes" />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {activeTab === 0 && <MetricsView />}
          {activeTab === 1 && <MilestonesView />}
          {activeTab === 2 && <NotesView />}
        </Box>
      </ResizableDrawerContent>
    );
  },
);
